import { useEffect, useRef, useState, useCallback, RefObject } from 'react';
import { Panel, PanelPhysics } from '../pages/landing-page/components/app-panels/panel';

const MIN_SPEED = 0.8;
const MAX_SPEED = 1.8;

function randomVelocity(): number {
    const magnitude = MIN_SPEED + Math.random() * (MAX_SPEED - MIN_SPEED);
    const direction = Math.sign(Math.random() - 0.5) || 1;
    return magnitude * direction;
}

interface PanelSize {
    w: number;
    h: number;
}

interface UseBounceReturn {
    positions: Record<string, { x: number; y: number }>;
    setHovered: (id: string | null) => void;
}

export default function useBounce(
    panels: Panel[],
    containerRef: RefObject<HTMLElement>,
    panelSize: PanelSize
): UseBounceReturn {
    const physicsRef = useRef<Record<string, PanelPhysics>>({});
    const hoveredRef = useRef<string | null>(null);
    const rafRef = useRef<number>(0);
    const [positions, setPositions] = useState<Record<string, { x: number; y: number }>>({});

    const setHovered = useCallback((id: string | null) => {
        hoveredRef.current = id;
    }, []);

    useEffect(() => {
        const container = containerRef.current;
        if (!container || panels.length === 0) return;

        const { width: cw, height: ch } = container.getBoundingClientRect();

        // Initialize physics for each panel
        panels.forEach((panel) => {
            const maxX = cw - panelSize.w;
            const maxY = ch - panelSize.h;
            physicsRef.current[panel.id] = {
                x: Math.random() * Math.max(maxX, 0),
                y: Math.random() * Math.max(maxY, 0),
                vx: randomVelocity(),
                vy: randomVelocity(),
                width: panelSize.w,
                height: panelSize.h,
            };
        });

        // Snapshot initial positions
        const initialPositions: Record<string, { x: number; y: number }> = {};
        panels.forEach(({ id }) => {
            initialPositions[id] = {
                x: physicsRef.current[id].x,
                y: physicsRef.current[id].y,
            };
        });
        setPositions(initialPositions);

        function tick() {
            const { width: cw, height: ch } = container!.getBoundingClientRect();

            const next: Record<string, { x: number; y: number }> = {};

            panels.forEach(({ id }) => {
                const p = physicsRef.current[id];
                if (!p) return;

                if (hoveredRef.current === id) {
                    next[id] = { x: p.x, y: p.y };
                    return;
                }

                let { x, y, vx, vy } = p;

                x += vx;
                y += vy;

                const maxX = cw - p.width;
                const maxY = ch - p.height;

                if (x <= 0) { x = 0; vx = Math.abs(vx); }
                else if (x >= maxX) { x = maxX; vx = -Math.abs(vx); }

                if (y <= 0) { y = 0; vy = Math.abs(vy); }
                else if (y >= maxY) { y = maxY; vy = -Math.abs(vy); }

                p.x = x; p.y = y; p.vx = vx; p.vy = vy;
                next[id] = { x, y };
            });

            setPositions(next);
            rafRef.current = requestAnimationFrame(tick);
        }

        rafRef.current = requestAnimationFrame(tick);

        return () => cancelAnimationFrame(rafRef.current);
    }, [panels, containerRef, panelSize.w, panelSize.h]);

    return { positions, setHovered };
}
